import React, { useState, useEffect, useCallback } from 'react';
import { useAppSelector } from '../store/hooks';
import { adminApiService } from '../services/adminApiService';
import { FaWallet, FaUpload, FaHistory, FaUsers, FaCheckCircle, FaTimesCircle, FaSpinner, FaSearch, FaFileCsv, FaCoins, FaCopy, FaKey, FaTrash, FaEye, FaEyeSlash } from 'react-icons/fa';
import './AdminCryptoPayouts.css';

interface CryptoWallet {
  id: number;
  blockchain: string;
  address: string;
  balance: number;
  createdAt: string;
  status: string;
}

interface RetailUser {
  id: number;
  username: string;
  email: string;
  walletAddress?: string;
  coinType?: string;
  balance?: number;
  accountStatus: string;
}

interface PayoutRow {
  userId: string;
  walletAddress: string;
  token: string;
  amount: string;
  status?: 'valid' | 'invalid';
  error?: string;
}

interface PayoutBatch {
  id: number;
  batchId: string;
  date: string;
  token: string;
  totalAmount: number;
  successCount: number;
  failureCount: number;
  status: string;
  payouts?: PayoutTransaction[];
}

interface PayoutTransaction {
  id: number;
  userId: number;
  walletAddress: string;
  token: string;
  amount: number;
  status: 'success' | 'failed' | 'pending';
  txHash?: string;
  error?: string;
}

/** CoinGecko `ids` for /simple/price (lowercase blockchain label → id) */
const COINGECKO_CHAIN_IDS: Record<string, string> = {
  ethereum: 'ethereum',
  bitcoin: 'bitcoin',
  polygon: 'matic-network',
  stellar: 'stellar',
  cardano: 'cardano',
  solana: 'solana',
};

const AdminCryptoPayouts: React.FC = () => {
  const adminUser = useAppSelector(state => state.auth.adminUser);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'wallets' | 'batch' | 'history'>('dashboard');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Dashboard state
  const [retailUsers, setRetailUsers] = useState<RetailUser[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCoin, setFilterCoin] = useState<string>('all');
  
  // Wallet management state
  const [adminWallets, setAdminWallets] = useState<CryptoWallet[]>([]);
  const [showCreateWalletModal, setShowCreateWalletModal] = useState(false);
  const [selectedBlockchain, setSelectedBlockchain] = useState<string>('');
  const [isCreatingWallet, setIsCreatingWallet] = useState(false);
  const [showPrivateKeyModal, setShowPrivateKeyModal] = useState(false);
  const [selectedWalletForExport, setSelectedWalletForExport] = useState<CryptoWallet | null>(null);
  const [privateKey, setPrivateKey] = useState<string>('');
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [isLoadingPrivateKey, setIsLoadingPrivateKey] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedWalletForDelete, setSelectedWalletForDelete] = useState<CryptoWallet | null>(null);
  const [isDeletingWallet, setIsDeletingWallet] = useState(false);

  /** USD spot price per 1 unit of native asset (e.g. one ETH), keyed by lowercase blockchain name */
  const [cryptoUsdPrices, setCryptoUsdPrices] = useState<Record<string, number>>({});
  const [usdPricesLoading, setUsdPricesLoading] = useState(false);
  const [usdPricesError, setUsdPricesError] = useState<string | null>(null);
  
  // Batch payout state
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [parsedRows, setParsedRows] = useState<PayoutRow[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedFundingWallet, setSelectedFundingWallet] = useState<string>('');
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionProgress, setExecutionProgress] = useState<{ completed: number; total: number } | null>(null);
  
  // History state
  const [payoutHistory, setPayoutHistory] = useState<PayoutBatch[]>([]);
  const [expandedBatch, setExpandedBatch] = useState<number | null>(null);

  // Fetch retail users with crypto wallets
  const fetchRetailUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApiService.get('/admin/crypto/users');
      if (response.success && response.data) {
        setRetailUsers(response.data);
      } else {
        setError(response.error || 'Failed to fetch retail users');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch retail users');
      console.error('Error fetching retail users:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch admin wallets
  const fetchAdminWallets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApiService.get('/admin/crypto/wallets/list');
      console.log('AdminCryptoPayouts: fetchAdminWallets response:', response);
      if (response.success && response.data) {
        console.log('AdminCryptoPayouts: Setting admin wallets:', response.data);
        setAdminWallets(response.data || []);
      } else {
        console.log('AdminCryptoPayouts: No wallets found or error:', response.error);
        setAdminWallets([]); // Ensure it's an empty array
        setError(response.error || 'Failed to fetch admin wallets');
      }
    } catch (err: any) {
      console.error('AdminCryptoPayouts: Error fetching admin wallets:', err);
      setAdminWallets([]); // Ensure it's an empty array on error
      setError(err.message || 'Failed to fetch admin wallets');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSpotUsdPrices = useCallback(async (wallets: CryptoWallet[]) => {
    const geoIds = Array.from(
      new Set(
        wallets
          .map((w) => COINGECKO_CHAIN_IDS[w.blockchain?.toLowerCase() ?? ''])
          .filter((id): id is string => Boolean(id))
      )
    );
    if (geoIds.length === 0) {
      setCryptoUsdPrices({});
      return;
    }
    setUsdPricesLoading(true);
    setUsdPricesError(null);
    try {
      const url = `https://api.coingecko.com/api/v3/simple/price?ids=${encodeURIComponent(
        geoIds.join(',')
      )}&vs_currencies=usd`;
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error(`Price request failed (${res.status})`);
      }
      const data: Record<string, { usd?: number }> = await res.json();
      const byChain: Record<string, number> = {};
      for (const w of wallets) {
        const chain = w.blockchain?.toLowerCase() ?? '';
        const geoId = COINGECKO_CHAIN_IDS[chain];
        const usd = geoId && data[geoId]?.usd != null ? data[geoId].usd : undefined;
        if (usd != null) {
          byChain[chain] = usd;
        }
      }
      setCryptoUsdPrices(byChain);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Could not load USD rates';
      setUsdPricesError(msg);
      setCryptoUsdPrices({});
    } finally {
      setUsdPricesLoading(false);
    }
  }, []);

  // Create admin wallet
  const handleCreateWallet = async () => {
    if (!selectedBlockchain) {
      setError('Please select a blockchain');
      return;
    }

    setIsCreatingWallet(true);
    setError(null);
    try {
      const response = await adminApiService.post('/admin/crypto/wallets/create', {
        blockchain: selectedBlockchain
      });
      
      if (response.success && response.data) {
        await fetchAdminWallets();
        setShowCreateWalletModal(false);
        setSelectedBlockchain('');
      } else {
        setError(response.error || 'Failed to create wallet');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create wallet');
      console.error('Error creating wallet:', err);
    } finally {
      setIsCreatingWallet(false);
    }
  };

  // Copy wallet address to clipboard
  const handleCopyAddress = async (address: string) => {
    try {
      await navigator.clipboard.writeText(address);
      // You could add a toast notification here
      alert('Address copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy address:', err);
      alert('Failed to copy address');
    }
  };

  // Export private key
  const handleExportPrivateKey = async (wallet: CryptoWallet) => {
    setSelectedWalletForExport(wallet);
    setShowPrivateKeyModal(true);
    setPrivateKey('');
    setShowPrivateKey(false);
    setIsLoadingPrivateKey(true);
    setError(null);

    try {
      const response = await adminApiService.get(`/admin/crypto/wallets/${wallet.id}/private-key`);
      if (response.success && response.data) {
        setPrivateKey(response.data.privateKey);
      } else {
        setError(response.error || 'Failed to retrieve private key');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to retrieve private key');
      console.error('Error fetching private key:', err);
    } finally {
      setIsLoadingPrivateKey(false);
    }
  };

  // Delete wallet
  const handleDeleteWallet = async () => {
    if (!selectedWalletForDelete) return;

    setIsDeletingWallet(true);
    setError(null);
    try {
      const response = await adminApiService.delete(`/admin/crypto/wallets/${selectedWalletForDelete.id}`);
      if (response.success) {
        await fetchAdminWallets();
        setShowDeleteModal(false);
        setSelectedWalletForDelete(null);
      } else {
        setError(response.error || 'Failed to delete wallet');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete wallet');
      console.error('Error deleting wallet:', err);
    } finally {
      setIsDeletingWallet(false);
    }
  };

  // Copy private key to clipboard
  const handleCopyPrivateKey = async () => {
    try {
      await navigator.clipboard.writeText(privateKey);
      alert('Private key copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy private key:', err);
      alert('Failed to copy private key');
    }
  };

  // Handle CSV file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }

    setCsvFile(file);
    setError(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      parseCSV(text);
    };
    reader.readAsText(file);
  };

  // Parse CSV file
  const parseCSV = (csvText: string) => {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      setError('CSV file must contain at least a header row and one data row');
      return;
    }

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const requiredHeaders = ['user_id', 'wallet_address', 'token', 'amount'];
    const hasAllHeaders = requiredHeaders.every(h => 
      headers.some(header => header.includes(h.replace('_', '')))
    );

    if (!hasAllHeaders) {
      setError('CSV must contain columns: user_id, wallet_address, token, amount');
      return;
    }

    const rows: PayoutRow[] = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      if (values.length < 4) continue;

      const row: PayoutRow = {
        userId: values[0] || '',
        walletAddress: values[1] || '',
        token: values[2]?.toUpperCase() || '',
        amount: values[3] || '',
        status: 'valid'
      };

      // Validate row
      if (!row.userId || !row.walletAddress || !row.token || !row.amount) {
        row.status = 'invalid';
        row.error = 'Missing required fields';
      } else if (!['USDT', 'USDC', 'ETH'].includes(row.token)) {
        row.status = 'invalid';
        row.error = 'Invalid token. Must be USDT, USDC, or ETH';
      } else if (isNaN(parseFloat(row.amount)) || parseFloat(row.amount) <= 0) {
        row.status = 'invalid';
        row.error = 'Invalid amount. Must be a positive number';
      }

      rows.push(row);
    }

    setParsedRows(rows);
    if (rows.length === 0) {
      setError('No valid rows found in CSV file');
    }
  };

  // Upload and validate batch
  const handleUploadBatch = async () => {
    if (parsedRows.length === 0) {
      setError('No valid rows to upload');
      return;
    }

    const invalidRows = parsedRows.filter(r => r.status === 'invalid');
    if (invalidRows.length > 0) {
      setError(`Please fix ${invalidRows.length} invalid row(s) before uploading`);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await adminApiService.post('/admin/crypto/payouts/upload', {
        rows: parsedRows
      });

      if (response.success && response.data) {
        setShowConfirmModal(true);
      } else {
        setError(response.error || 'Failed to upload batch');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to upload batch');
      console.error('Error uploading batch:', err);
    } finally {
      setLoading(false);
    }
  };

  // Execute batch payouts
  const handleExecuteBatch = async () => {
    if (!selectedFundingWallet) {
      setError('Please select a funding wallet');
      return;
    }

    setIsExecuting(true);
    setError(null);
    setExecutionProgress({ completed: 0, total: parsedRows.length });

    try {
      const response = await adminApiService.post('/admin/crypto/payouts/execute', {
        fundingWalletId: selectedFundingWallet,
        rows: parsedRows
      });

      if (response.success && response.data) {
        setExecutionProgress({ completed: parsedRows.length, total: parsedRows.length });
        // Reset batch state
        setCsvFile(null);
        setParsedRows([]);
        setShowConfirmModal(false);
        // Refresh history
        await fetchPayoutHistory();
        // Switch to history tab
        setTimeout(() => {
          setActiveTab('history');
          setExecutionProgress(null);
        }, 2000);
      } else {
        setError(response.error || 'Failed to execute batch');
        setExecutionProgress(null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to execute batch');
      setExecutionProgress(null);
      console.error('Error executing batch:', err);
    } finally {
      setIsExecuting(false);
    }
  };

  // Fetch payout history
  const fetchPayoutHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApiService.get('/admin/crypto/payouts/history');
      if (response.success && response.data) {
        setPayoutHistory(response.data);
      } else {
        setError(response.error || 'Failed to fetch payout history');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch payout history');
      console.error('Error fetching payout history:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data on tab change
  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchRetailUsers();
    } else if (activeTab === 'wallets') {
      fetchAdminWallets();
    } else if (activeTab === 'history') {
      fetchPayoutHistory();
    }
  }, [activeTab, fetchRetailUsers, fetchAdminWallets, fetchPayoutHistory]);

  // Approximate USD value (spot from CoinGecko) for admin wallet cards
  useEffect(() => {
    if (activeTab !== 'wallets' || !adminWallets.length) {
      return;
    }
    fetchSpotUsdPrices(adminWallets);
  }, [activeTab, adminWallets, fetchSpotUsdPrices]);

  // Filter retail users
  const filteredUsers = retailUsers.filter(user => {
    const matchesSearch = !searchQuery || 
      user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.walletAddress?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCoin = filterCoin === 'all' || user.coinType === filterCoin;
    
    return matchesSearch && matchesCoin;
  });

  // Format currency - handles both fiat and crypto
  const formatCurrency = (amount: number, currency: string = 'USD') => {
    if (!currency || typeof amount !== 'number' || isNaN(amount)) {
      return '0.00';
    }

    // Map blockchain names to currency codes
    const currencyMap: { [key: string]: string } = {
      'ethereum': 'ETH',
      'stellar': 'XLM',
      'bitcoin': 'BTC',
      'polygon': 'MATIC',
      'solana': 'SOL',
      'cardano': 'ADA',
      'usdt': 'USDT',
      'usdc': 'USDC',
      'eth': 'ETH',
      'btc': 'BTC'
    };

    // Valid fiat currency codes
    const validFiatCurrencies = ['USD', 'EUR', 'GBP', 'JPY', 'CNY', 'AUD', 'CAD', 'CHF', 'INR', 'SGD'];

    // Normalize currency string
    const normalizedCurrency = currency.toLowerCase().trim();
    let currencyCode = currencyMap[normalizedCurrency] || currency.toUpperCase();

    // Check if it's a valid fiat currency code
    if (validFiatCurrencies.includes(currencyCode)) {
      try {
        // Use Intl.NumberFormat for fiat currencies
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currencyCode
        }).format(amount);
      } catch (e) {
        // Fallback if currency code is invalid
        return `${amount.toLocaleString('en-US', { 
          minimumFractionDigits: 2, 
          maximumFractionDigits: 2 
        })} ${currencyCode}`;
      }
    } else {
      // For crypto currencies, format as number with symbol
      return `${amount.toLocaleString('en-US', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 8 
      })} ${currencyCode}`;
    }
  };

  const formatUsdApprox = (usdAmount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(usdAmount);

  const getWalletUsdApprox = (wallet: CryptoWallet): number | null => {
    const chain = wallet.blockchain?.toLowerCase() ?? '';
    const px = cryptoUsdPrices[chain];
    if (px == null || typeof wallet.balance !== 'number' || Number.isNaN(wallet.balance)) {
      return null;
    }
    return wallet.balance * px;
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="admin-crypto-payouts">
      <div className="admin-crypto-payouts-header">
        <h1>Crypto Payouts</h1>
        <p>Manage crypto payouts to retail users</p>
      </div>

      {/* Tabs */}
      <div className="admin-crypto-payouts-tabs">
        <button
          className={activeTab === 'dashboard' ? 'active' : ''}
          onClick={() => setActiveTab('dashboard')}
        >
          <FaUsers /> Dashboard
        </button>
        <button
          className={activeTab === 'wallets' ? 'active' : ''}
          onClick={() => setActiveTab('wallets')}
        >
          <FaWallet /> My Wallets
        </button>
        <button
          className={activeTab === 'batch' ? 'active' : ''}
          onClick={() => setActiveTab('batch')}
        >
          <FaUpload /> Batch Payout
        </button>
        <button
          className={activeTab === 'history' ? 'active' : ''}
          onClick={() => setActiveTab('history')}
        >
          <FaHistory /> History
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="admin-crypto-payouts-error">
          <FaTimesCircle /> {error}
        </div>
      )}

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div className="admin-crypto-payouts-content">
          <div className="admin-crypto-payouts-filters">
            <div className="search-box">
              <FaSearch />
              <input
                type="text"
                placeholder="Search by username, email, or wallet address..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <select
              value={filterCoin}
              onChange={(e) => setFilterCoin(e.target.value)}
            >
              <option value="all">All Coins</option>
              <option value="USDT">USDT</option>
              <option value="USDC">USDC</option>
              <option value="ETH">ETH</option>
            </select>
          </div>

          {loading ? (
            <div className="loading-spinner">
              <FaSpinner className="spinning" />
              <p>Loading retail users...</p>
            </div>
          ) : (
            <div className="retail-users-table">
              <table>
                <thead>
                  <tr>
                    <th>User Name</th>
                    <th>Email</th>
                    <th>Wallet Address</th>
                    <th>Coin Type</th>
                    <th>Balance</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="empty-state">
                        No retail users found
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((user) => (
                      <tr key={user.id}>
                        <td>{user.username}</td>
                        <td>{user.email}</td>
                        <td className="wallet-address">
                          {user.walletAddress || 'N/A'}
                        </td>
                        <td>
                          <span className="coin-badge">{user.coinType || 'N/A'}</span>
                        </td>
                        <td>
                          {user.balance !== undefined
                            ? formatCurrency(user.balance, user.coinType || 'USD')
                            : 'N/A'}
                        </td>
                        <td>
                          <span className={`status-badge ${user.accountStatus.toLowerCase()}`}>
                            {user.accountStatus}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Wallets Tab */}
      {activeTab === 'wallets' && (
        <div className="admin-crypto-payouts-content" style={{ padding: '24px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '32px',
            width: '100%',
            flexWrap: 'wrap',
            gap: '16px',
            paddingBottom: '16px',
            borderBottom: '2px solid #e5e7eb'
          }}>
            <h2 style={{ margin: 0, flex: '1 1 auto', fontSize: '24px', fontWeight: '700' }}>Admin Crypto Wallets</h2>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Create Admin Wallet button clicked');
                setShowCreateWalletModal(true);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '14px 28px',
                background: '#086afb',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: 'pointer',
                fontSize: '15px',
                boxShadow: '0 4px 6px rgba(8, 106, 251, 0.3)',
                flexShrink: 0,
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#0756d3';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#086afb';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <FaWallet style={{ fontSize: '16px' }} /> Create Admin Wallet
            </button>
          </div>

          {loading ? (
            <div className="loading-spinner">
              <FaSpinner className="spinning" />
              <p>Loading wallets...</p>
            </div>
          ) : (adminWallets.length === 0 || !adminWallets) ? (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              padding: '80px 40px',
              minHeight: '500px',
              width: '100%',
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              border: '2px dashed #e5e7eb'
            }}>
              <div style={{ 
                width: '120px', 
                height: '120px', 
                borderRadius: '50%', 
                backgroundColor: '#f3f4f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '32px'
              }}>
                <FaWallet size={64} style={{ color: '#9ca3af' }} />
              </div>
              <h3 style={{ 
                fontSize: '24px', 
                fontWeight: '700',
                color: '#1f2937', 
                marginBottom: '12px',
                textAlign: 'center'
              }}>
                No Wallets Yet
              </h3>
              <p style={{ 
                fontSize: '16px', 
                color: '#6b7280', 
                marginBottom: '40px',
                textAlign: 'center',
                maxWidth: '500px',
                lineHeight: '1.6'
              }}>
                No admin wallets found. Create your first wallet to start managing crypto payouts.
              </p>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Create Your First Wallet button clicked');
                  setShowCreateWalletModal(true);
                }}
                style={{
                  padding: '18px 36px',
                  fontSize: '17px',
                  fontWeight: '700',
                  minWidth: '300px',
                  background: '#086afb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  boxShadow: '0 6px 12px rgba(8, 106, 251, 0.4)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#0756d3';
                  e.currentTarget.style.transform = 'translateY(-3px) scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 8px 16px rgba(8, 106, 251, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#086afb';
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 6px 12px rgba(8, 106, 251, 0.4)';
                }}
              >
                <FaWallet style={{ fontSize: '20px' }} /> Create Your First Wallet
              </button>
            </div>
          ) : (
            <div className="wallets-grid">
              {usdPricesError && (
                <div
                  role="status"
                  style={{
                    gridColumn: '1 / -1',
                    fontSize: '13px',
                    color: '#b45309',
                    background: '#fffbeb',
                    border: '1px solid #fcd34d',
                    borderRadius: '8px',
                    padding: '10px 14px',
                  }}
                >
                  USD estimate unavailable: {usdPricesError}
                </div>
              )}
              {usdPricesLoading &&
                adminWallets.some((w) => COINGECKO_CHAIN_IDS[w.blockchain?.toLowerCase() ?? '']) && (
                  <div
                    role="status"
                    style={{
                      gridColumn: '1 / -1',
                      fontSize: '13px',
                      color: '#4b5563',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <FaSpinner className="spinning" />
                    Loading USD spot estimates (CoinGecko)…
                  </div>
                )}
              {adminWallets.map((wallet) => (
                  <div key={wallet.id} className="wallet-card">
                    <div className="wallet-header">
                      <FaCoins />
                      <span className="wallet-token">{wallet.blockchain.toUpperCase()}</span>
                    </div>
                    <div className="wallet-address" style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      cursor: 'pointer'
                    }} onClick={() => handleCopyAddress(wallet.address)}>
                      <span>{wallet.address}</span>
                      <FaCopy size={12} style={{ color: '#6b7280' }} />
                    </div>
                    <div className="wallet-balance">
                      {formatCurrency(wallet.balance, wallet.blockchain)}
                    </div>
                    {!usdPricesLoading &&
                      (() => {
                        const usd = getWalletUsdApprox(wallet);
                        if (usd == null) {
                          return COINGECKO_CHAIN_IDS[wallet.blockchain?.toLowerCase() ?? ''] ? (
                            <div className="wallet-balance-usd-muted">USD estimate unavailable</div>
                          ) : null;
                        }
                        return (
                          <div className="wallet-balance-usd" title="Spot approximation via CoinGecko">
                            ≈ {formatUsdApprox(usd)}{' '}
                            <span style={{ fontWeight: 500, fontSize: '12px', color: '#6b7280' }}>USD</span>
                          </div>
                        );
                      })()}
                    <div className="wallet-footer">
                      <span className="wallet-status">{wallet.status || 'Active'}</span>
                      <span className="wallet-date">{formatDate(wallet.createdAt)}</span>
                    </div>
                    <div className="wallet-actions" style={{
                      display: 'flex',
                      gap: '8px',
                      marginTop: '16px',
                      paddingTop: '16px',
                      borderTop: '1px solid #e5e7eb'
                    }}>
                      <button
                        type="button"
                        onClick={() => handleCopyAddress(wallet.address)}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          background: '#f3f4f6',
                          color: '#374151',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                        title="Copy Address"
                      >
                        <FaCopy size={12} /> Copy
                      </button>
                      <button
                        type="button"
                        onClick={() => handleExportPrivateKey(wallet)}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          background: '#fef3c7',
                          color: '#92400e',
                          border: '1px solid #fbbf24',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                        title="Export Private Key"
                      >
                        <FaKey size={12} /> Export Key
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedWalletForDelete(wallet);
                          setShowDeleteModal(true);
                        }}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          background: '#fee2e2',
                          color: '#991b1b',
                          border: '1px solid #fca5a5',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                        title="Delete Wallet"
                      >
                        <FaTrash size={12} /> Delete
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Batch Payout Tab */}
      {activeTab === 'batch' && (
        <div className="admin-crypto-payouts-content">
          <div className="batch-upload-section">
            <h2>Upload Batch Payout CSV</h2>
            <p>Upload a CSV file with payout details. Format: user_id, wallet_address, token, amount</p>
            
            <div className="file-upload-area">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                id="csv-upload"
                style={{ display: 'none' }}
              />
              <label htmlFor="csv-upload" className="upload-button">
                <FaFileCsv /> {csvFile ? csvFile.name : 'Choose CSV File'}
              </label>
            </div>

            {parsedRows.length > 0 && (
              <div className="batch-preview">
                <h3>Preview ({parsedRows.length} rows)</h3>
                <div className="preview-table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>User ID</th>
                        <th>Wallet Address</th>
                        <th>Token</th>
                        <th>Amount</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedRows.map((row, index) => (
                        <tr key={index} className={row.status === 'invalid' ? 'invalid-row' : ''}>
                          <td>{row.userId}</td>
                          <td className="wallet-address">{row.walletAddress}</td>
                          <td>{row.token}</td>
                          <td>{row.amount}</td>
                          <td>
                            {row.status === 'valid' ? (
                              <FaCheckCircle className="valid-icon" />
                            ) : (
                              <span className="error-text">{row.error}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button
                  className="btn-primary"
                  onClick={handleUploadBatch}
                  disabled={loading || parsedRows.some(r => r.status === 'invalid')}
                >
                  {loading ? <FaSpinner className="spinning" /> : <FaUpload />} Upload & Validate
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="admin-crypto-payouts-content">
          <h2>Payout History</h2>
          {loading ? (
            <div className="loading-spinner">
              <FaSpinner className="spinning" />
              <p>Loading history...</p>
            </div>
          ) : (
            <div className="history-table">
              <table>
                <thead>
                  <tr>
                    <th>Batch ID</th>
                    <th>Date</th>
                    <th>Token</th>
                    <th>Total Amount</th>
                    <th>Success</th>
                    <th>Failed</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payoutHistory.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="empty-state">
                        No payout history found
                      </td>
                    </tr>
                  ) : (
                    payoutHistory.map((batch) => (
                      <React.Fragment key={batch.id}>
                        <tr>
                          <td>{batch.batchId}</td>
                          <td>{formatDate(batch.date)}</td>
                          <td><span className="coin-badge">{batch.token}</span></td>
                          <td>{formatCurrency(batch.totalAmount, batch.token)}</td>
                          <td><span className="success-count">{batch.successCount}</span></td>
                          <td><span className="failure-count">{batch.failureCount}</span></td>
                          <td>
                            <span className={`status-badge ${batch.status.toLowerCase()}`}>
                              {batch.status}
                            </span>
                          </td>
                          <td>
                            <button
                              className="btn-link"
                              onClick={() => setExpandedBatch(expandedBatch === batch.id ? null : batch.id)}
                            >
                              {expandedBatch === batch.id ? 'Hide' : 'View'} Details
                            </button>
                          </td>
                        </tr>
                        {expandedBatch === batch.id && batch.payouts && (
                          <tr>
                            <td colSpan={8}>
                              <div className="batch-details">
                                <h4>Individual Payouts</h4>
                                <table>
                                  <thead>
                                    <tr>
                                      <th>User ID</th>
                                      <th>Wallet Address</th>
                                      <th>Amount</th>
                                      <th>Status</th>
                                      <th>TX Hash</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {batch.payouts.map((payout) => (
                                      <tr key={payout.id}>
                                        <td>{payout.userId}</td>
                                        <td className="wallet-address">{payout.walletAddress}</td>
                                        <td>{formatCurrency(payout.amount, payout.token)}</td>
                                        <td>
                                          <span className={`status-badge ${payout.status}`}>
                                            {payout.status}
                                          </span>
                                        </td>
                                        <td>
                                          {payout.txHash ? (
                                            <a
                                              href={`https://etherscan.io/tx/${payout.txHash}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="tx-link"
                                            >
                                              {payout.txHash.substring(0, 10)}...
                                            </a>
                                          ) : (
                                            payout.error || 'N/A'
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Create Wallet Modal */}
      {showCreateWalletModal && (
        <div className="modal-overlay" onClick={() => setShowCreateWalletModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Create Admin Wallet</h2>
            <div className="form-group">
              <label>Select Blockchain</label>
              <select
                value={selectedBlockchain}
                onChange={(e) => setSelectedBlockchain(e.target.value)}
              >
                <option value="">Choose...</option>
                <option value="Ethereum">Ethereum (ETH, USDT, USDC)</option>
                <option value="Bitcoin">Bitcoin (BTC)</option>
                <option value="Polygon">Polygon (MATIC)</option>
                <option value="Stellar">Stellar (XLM)</option>
                <option value="Solana">Solana (SOL)</option>
                <option value="Cardano">Cardano (ADA)</option>
              </select>
            </div>
            <div className="modal-actions" style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              marginTop: '24px',
              paddingTop: '16px',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setShowCreateWalletModal(false);
                  setSelectedBlockchain('');
                }}
                style={{
                  padding: '10px 20px',
                  background: 'white',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleCreateWallet}
                disabled={!selectedBlockchain || isCreatingWallet}
                style={{
                  padding: '10px 20px',
                  background: '#086afb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '500',
                  cursor: !selectedBlockchain || isCreatingWallet ? 'not-allowed' : 'pointer',
                  opacity: !selectedBlockchain || isCreatingWallet ? 0.6 : 1
                }}
              >
                {isCreatingWallet ? <FaSpinner className="spinning" /> : 'Create Wallet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Private Key Export Modal */}
      {showPrivateKeyModal && selectedWalletForExport && (
        <div className="modal-overlay" onClick={() => {
          setShowPrivateKeyModal(false);
          setSelectedWalletForExport(null);
          setPrivateKey('');
          setShowPrivateKey(false);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <h2>Export Private Key</h2>
            <div style={{ marginBottom: '20px' }}>
              <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '12px' }}>
                <strong>Warning:</strong> Never share your private key with anyone. Anyone with access to your private key can control your wallet.
              </p>
              <div style={{ 
                padding: '12px', 
                background: '#fef3c7', 
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <p style={{ fontSize: '13px', color: '#92400e', margin: 0 }}>
                  <strong>Blockchain:</strong> {selectedWalletForExport.blockchain}<br />
                  <strong>Address:</strong> {selectedWalletForExport.address}
                </p>
              </div>
            </div>

            {isLoadingPrivateKey ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <FaSpinner className="spinning" style={{ fontSize: '24px', color: '#086afb' }} />
                <p style={{ marginTop: '12px', color: '#6b7280' }}>Loading private key...</p>
              </div>
            ) : error ? (
              <div style={{ 
                padding: '12px', 
                background: '#fee2e2', 
                borderRadius: '8px',
                color: '#991b1b',
                marginBottom: '16px'
              }}>
                {error}
              </div>
            ) : privateKey ? (
              <div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>Private Key</span>
                    <button
                      type="button"
                      onClick={() => setShowPrivateKey(!showPrivateKey)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#086afb',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '12px'
                      }}
                    >
                      {showPrivateKey ? <FaEyeSlash /> : <FaEye />}
                      {showPrivateKey ? 'Hide' : 'Show'}
                    </button>
                  </label>
                  <div style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <input
                      type={showPrivateKey ? 'text' : 'password'}
                      value={privateKey}
                      readOnly
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        background: '#f9fafb'
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleCopyPrivateKey}
                      style={{
                        padding: '12px',
                        background: '#086afb',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                      title="Copy Private Key"
                    >
                      <FaCopy /> Copy
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="modal-actions" style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              marginTop: '24px',
              paddingTop: '16px',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setShowPrivateKeyModal(false);
                  setSelectedWalletForExport(null);
                  setPrivateKey('');
                  setShowPrivateKey(false);
                }}
                style={{
                  padding: '10px 20px',
                  background: 'white',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Wallet Confirmation Modal */}
      {showDeleteModal && selectedWalletForDelete && (
        <div className="modal-overlay" onClick={() => {
          if (!isDeletingWallet) {
            setShowDeleteModal(false);
            setSelectedWalletForDelete(null);
          }
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Delete Wallet</h2>
            <div style={{ marginBottom: '20px' }}>
              <p style={{ color: '#991b1b', fontSize: '14px', marginBottom: '12px' }}>
                <strong>Warning:</strong> This action cannot be undone. The wallet will be permanently deleted.
              </p>
              <div style={{ 
                padding: '12px', 
                background: '#fee2e2', 
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <p style={{ fontSize: '13px', color: '#991b1b', margin: 0 }}>
                  <strong>Blockchain:</strong> {selectedWalletForDelete.blockchain}<br />
                  <strong>Address:</strong> {selectedWalletForDelete.address}<br />
                  <strong>Balance:</strong> {formatCurrency(selectedWalletForDelete.balance, selectedWalletForDelete.blockchain)}
                  {(() => {
                    const delUsd = getWalletUsdApprox(selectedWalletForDelete);
                    return delUsd != null ? (
                      <>
                        <br />
                        <strong>≈ USD:</strong> {formatUsdApprox(delUsd)}
                      </>
                    ) : null;
                  })()}
                </p>
              </div>
              {selectedWalletForDelete.balance > 0 && (
                <p style={{ 
                  color: '#dc2626', 
                  fontSize: '13px',
                  fontWeight: '600',
                  marginTop: '12px'
                }}>
                  ⚠️ This wallet has a balance. Make sure to transfer funds before deleting.
                </p>
              )}
            </div>

            {error && (
              <div style={{ 
                padding: '12px', 
                background: '#fee2e2', 
                borderRadius: '8px',
                color: '#991b1b',
                marginBottom: '16px'
              }}>
                {error}
              </div>
            )}

            <div className="modal-actions" style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              marginTop: '24px',
              paddingTop: '16px',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  if (!isDeletingWallet) {
                    setShowDeleteModal(false);
                    setSelectedWalletForDelete(null);
                  }
                }}
                disabled={isDeletingWallet}
                style={{
                  padding: '10px 20px',
                  background: 'white',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontWeight: '500',
                  cursor: isDeletingWallet ? 'not-allowed' : 'pointer',
                  opacity: isDeletingWallet ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleDeleteWallet}
                disabled={isDeletingWallet}
                style={{
                  padding: '10px 20px',
                  background: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '500',
                  cursor: isDeletingWallet ? 'not-allowed' : 'pointer',
                  opacity: isDeletingWallet ? 0.6 : 1
                }}
              >
                {isDeletingWallet ? (
                  <>
                    <FaSpinner className="spinning" /> Deleting...
                  </>
                ) : (
                  <>
                    <FaTrash /> Delete Wallet
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Execute Modal */}
      {showConfirmModal && (
        <div className="modal-overlay" onClick={() => !isExecuting && setShowConfirmModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Confirm Batch Payout</h2>
            <div className="form-group">
              <label>Select Funding Wallet</label>
              <select
                value={selectedFundingWallet}
                onChange={(e) => setSelectedFundingWallet(e.target.value)}
                disabled={isExecuting}
              >
                <option value="">Choose wallet...</option>
                {adminWallets.map((wallet) => (
                  <option key={wallet.id} value={wallet.id.toString()}>
                    {wallet.blockchain.toUpperCase()} - {wallet.address.substring(0, 10)}... (Balance: {formatCurrency(wallet.balance, wallet.blockchain)})
                  </option>
                ))}
              </select>
            </div>
            <div className="batch-summary">
              <p><strong>Total Payouts:</strong> {parsedRows.length}</p>
              <p><strong>Total Amount:</strong> {formatCurrency(
                parsedRows.reduce((sum, row) => sum + parseFloat(row.amount || '0'), 0),
                parsedRows[0]?.token || 'USD'
              )}</p>
            </div>
            {executionProgress && (
              <div className="execution-progress">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${(executionProgress.completed / executionProgress.total) * 100}%` }}
                  />
                </div>
                <p>
                  Processing: {executionProgress.completed} / {executionProgress.total}
                </p>
              </div>
            )}
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => {
                  setShowConfirmModal(false);
                  setExecutionProgress(null);
                }}
                disabled={isExecuting}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleExecuteBatch}
                disabled={!selectedFundingWallet || isExecuting}
              >
                {isExecuting ? (
                  <>
                    <FaSpinner className="spinning" /> Executing...
                  </>
                ) : (
                  'Confirm & Send'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCryptoPayouts;

